import React, { FC, useContext } from 'react';
import AudioPlayerContext from '../../context/AudioPlayerContext';
import Icon from '../Icons/Icon';
import IconPlay from 'components/Icons/IconPlay';
import { removeZeroFromDuration } from 'utils/utils';
import IconPauseRedBtn from 'components/Icons/IconPauseRedBtn';

interface Props {
  audioSource: string;
  audioSubtitle?: string;
  audioTitle?: string;
  extraText?: string;
  elementClass?: string;
  label?: string;
  size?: string;
  showTitle?: boolean;
  showDuration?: string;
  iconWidth?: string;
  playBtnSm?: boolean;
  audioThumbnailSrc?: string;
  audioThumbnailAlt?: string | null;
}

const AudioPlayButton: FC<Props> = ({
  audioSource,
  audioSubtitle,
  audioTitle,
  extraText,
  audioThumbnailSrc,
  audioThumbnailAlt,
  label,
  size,
  showTitle = false,
  showDuration,
  iconWidth = '1.25rem',
  playBtnSm,
}) => {
  // Block single .m3u/.m3u8 URLs (no fallback support)
  // Allow arrays (have fallback support)
  if (typeof audioSource === 'string' && audioSource.match(/\.m3u8?/)) {
    return null;
  }

  const AudioContext = useContext(AudioPlayerContext);

  // Full audio source with podtrac prefix for comparison
  const fullAudioSource = 'https://dts.podtrac.com/redirect.mp3/' + audioSource;

  const buttonInner = (context: any) /* TODO: make it not any */ => {
    if (context.isAudioPlaying && fullAudioSource === context.audioSource) {
      return (
        <span className="btn-round-inner btn-round-inner-pause">
          <Icon name="pause" />
          {!label && <span className="invisible">Pause</span>}
        </span>
      );
    } else {
      return (
        <span className="btn-round-inner btn-round-inner-play">
          <IconPlay
            color="var(--color-white)"
            width={iconWidth}
            height={iconWidth}
          />
          {!label && <span className="invisible">Play</span>}
        </span>
      );
    }
  };

  const buttonInnerTeaser = (context: any) /* TODO: make it not any */ => {
    if (context.isAudioPlaying && fullAudioSource === context.audioSource) {
      return (
        <span className="">
          <IconPauseRedBtn width={iconWidth} height={iconWidth} />
          {!label && <span className="invisible">Pause</span>}
        </span>
      );
    } else {
      return (
        <span className="">
          <IconPlay width={iconWidth} height={iconWidth} />
          {!label && <span className="invisible">Play</span>}
        </span>
      );
    }
  };

  const style = () => {
    if (size) {
      return { height: size, width: size };
    }
  };

  const updateAudio = (
    audioSource: string,
    audioTitle: string | undefined,
    audioSubtitle: string,
    audioThumbnailSrc: string | undefined,
    audioThumbnailAlt: string | undefined | null
  ) => {
    const fullSource = 'https://dts.podtrac.com/redirect.mp3/' + audioSource;

    const currentSource = Array.isArray(AudioContext.audioSource)
      ? AudioContext.audioSource[0]
      : AudioContext.audioSource;

    if (fullSource === currentSource) {
      AudioContext.togglePlaying();
    } else {
      AudioContext.switchAudio(
        fullSource,
        audioTitle,
        audioSubtitle,
        audioThumbnailSrc,
        audioThumbnailAlt ?? undefined
      );
    }
  };

  return (
    <AudioPlayerContext.Consumer>
      {(context) => (
        <button
          type="button"
          className={`btn-round ${context.isAudioPlaying ? 'playing' : ''} ${
            playBtnSm ? 'btn-audio-sm' : ''
          }`}
          onClick={(e) => {
            e.preventDefault();
            updateAudio(
              audioSource,
              audioTitle,
              audioSubtitle ?? ' ',
              audioThumbnailSrc,
              audioThumbnailAlt
            );
          }}
        >
          <span style={style()}>
            {playBtnSm ? buttonInnerTeaser(context) : buttonInner(context)}
          </span>
          {label && (
            <b>
              {context.isAudioPlaying &&
              (Array.isArray(context.audioSource)
                ? context.audioSource[0] === fullAudioSource
                : context.audioSource === fullAudioSource)
                ? 'Pause'
                : label}
            </b>
          )}
          <span>
            {showDuration && (
              <>{` [${removeZeroFromDuration(showDuration)}] `}</>
            )}
            {showTitle && (
              <span
                className="audio-player-title"
                aria-label={`- ${audioTitle}`} /* dash adds pause for screen readers */
              >
                {audioTitle}
              </span>
            )}
            {extraText && <span>{extraText}</span>}
          </span>
        </button>
      )}
    </AudioPlayerContext.Consumer>
  );
};

export default AudioPlayButton;
